import { colorTokens } from '@kariusdx/design-system';
import { createTheme, responsiveFontSizes, ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import Colors from './colors';
import { processHeaderTheme } from './header.theme';

// Create a theme instance.
let themeConfig: ThemeOptions = {
  typography: {
    allVariants: { color: '#4c4e56' },
    fontFamily: [
      "'Roboto'",
      "'Helvetica Neue'",
      "'Arial'",
      "'sans-serif'",
      "'Apple Color Emoji'",
      "'Segoe UI Emoji'",
      "'Segoe UI Symbol'",
      "'Arimo'",
    ].join(','),
    subtitle2: {
      fontWeight: 'bold',
      fontFamily: 'Arimo',
    },
    subtitle1: {
      fontFamily: 'Arimo',
    },
    h1: {
      fontFamily: 'Arimo',
    },
    h2: {
      fontFamily: 'Arimo',
    },
    h3: {
      fontFamily: 'Arimo',
    },
    h4: {
      fontFamily: 'Arimo',
      marginTop: 12,
    },
    h5: {
      fontFamily: 'Arimo',
    },
    h6: {
      fontFamily: 'Arimo',
    },
    body1: {
      fontFamily: 'Roboto',
    },
    body2: {
      fontFamily: 'Roboto',
      fontSize: 12,
    },
    caption: {
      fontFamily: 'Roboto',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 100,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
        colorSecondary: {
          color: Colors.white,
          border: '2px solid ' + Colors.teal,
          '&:hover': {
            border: '2px solid ' + Colors.darkTeal,
          },
          '&:focus': {
            border: '2px solid ' + Colors.darkTeal,
          },
        },
        outlinedSecondary: {
          fontWeight: 'normal',
          border: '1px solid transparent',
          paddingRight: '1px',
          paddingLeft: '1px',
          color: Colors.midGray,
          '& span.MuiChip-label': {
            color: Colors.black,
            paddingRight: 13,
          },
          '&:hover': {
            backgroundColor: Colors.teal10,
            border: '2px solid ' + Colors.teal,
            color: Colors.teal,
            marginRight: '0px',
            marginLeft: '0px',
          },
          '&:focus': {
            backgroundColor: Colors.teal10,
            border: '2px solid ' + Colors.teal,
            color: Colors.teal,
            marginRight: '0px',
            marginLeft: '0px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: 73,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          height: 73,
        },
        indicator: {
          height: 8,
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          textTransform: 'none',
        },
        // Is this right?

        iconWrapper: {
          color: Colors.white,
        },
        textColorInherit: {
          opacity: 1,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          minWidth: 300,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: '10px 0px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '6px 20px',
          '&.Mui-disabled': {
            color: Colors.blue,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        padding: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            backgroundColor: Colors.blue,
            color: Colors.white,
            '&:hover': {
              backgroundColor: Colors.blue,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiClockPicker: {
      styleOverrides: {
        root: {
          span: {
            color: '#4c4e56',
          },
        },
      },
    },
    MuiCalendarPicker: {
      styleOverrides: {
        root: {
          span: {
            color: '#4c4e56',
          },
        },
      },
    },
    // @ts-ignore, pickers library doesn't expose this
    // Needed for changing color of dates in picker header on mobile
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          span: {
            color: '#4c4e56',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: Colors.midGray,
        },
      },
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        adornedEnd: {
          paddingRight: 2,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: 14,
          marginTop: '2px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
  },
  palette: {
    background: {
      default: '#ffffff',
    },
    primary: {
      main: Colors.blue,
    },
    secondary: {
      main: Colors.teal,
      contrastText: Colors.white,
    },
    text: {
      secondary: Colors.white,
    },
    ...colorTokens,
  },
  spacing(factor: number) {
    return factor * 4;
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
};

themeConfig = processHeaderTheme(themeConfig);

let theme = createTheme(themeConfig);
theme = responsiveFontSizes(theme);

export default theme;
